import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useNavigate } from 'react-router-dom'
import { Box, Divider, List, ListItem, ListItemButton, ListItemIcon, ListItemText, ListItemAvatar, Avatar, Menu, Stack, Typography } from '@mui/material'
import { ListGroup } from 'react-bootstrap'
import { useAuth0 } from '@auth0/auth0-react'
import { Form } from 'antd'
import moment from 'moment'
import 'moment/locale/zh-hk'
import { saveAs } from 'file-saver'

import { MdLanguage, MdOutlineChangeCircle, MdViewModule } from "react-icons/md"
import { IoSettingsSharp, IoRainy } from "react-icons/io5"
import { IoIosWater, IoIosLogOut } from "react-icons/io"
import { FaTemperatureFull } from "react-icons/fa6"
import { PiList } from "react-icons/pi"
import { GoArrowSwitch } from "react-icons/go"

import { IconButtonComponent } from 'Shared/Components/Icon'
import PopupModal from 'Shared/Components/PopupModal'
import { PasswordInput, FormContainer } from 'Shared/Components/FormComponents'
import useLoop from 'Shared/Hooks/useLoop'
import useFetchData from 'Shared/Hooks/useFetchData'
import { apiUrl, webUrl } from 'Shared/utils'
import { showError, showSuccess } from 'Shared/Components/NotifyToast'
import useAuth from 'Shared/Hooks/useAuth'
import { RiAdminFill } from 'react-icons/ri'
import { IoDocumentText } from "react-icons/io5"

const Appbar = ({ mode, title, projects, userMetaData, setAppbarLoading, reload, appbarHeight }) => {
  const { user, loginWithRedirect, logout } = useAuth0()
  const { organization, project } = useParams()
  const { t, i18n } = useTranslation()
  const { send } = useFetchData()
  const navigate = useNavigate()
  const [form] = Form.useForm()
  const { role } = useAuth()

  const [weather, setWeather] = useState()
  const [time, setTime] = useState()
  const [icon, setIcon] = useState()
  const [favicon, setFavicon] = useState()
  const [anchorEl, setAnchorEl] = useState(null)

  const [projectModelOpen, setProjectModelOpen] = useState(false)
  const [languageModelOpen, setLanguageModelOpen] = useState(false)
  const [changePasswordModalOpen, setChangePasswordModalOpen] = useState(false)

  const getWeather = async () => {
    await Promise.all([
      send({ url: `${apiUrl}/common/weather`, method: "GET", returnType: "json" }).then(res =>
        res && setWeather(res)
      )
    ])
  }

  const getTime = () => {
    setTime(moment(new Date()))
  }

  const getIcon = async () => {
    await Promise.all([
      send({ url: `${apiUrl}/common/icon/${organization}`, method: "GET", returnType: "blob" }).then(res =>
        res && setIcon(URL.createObjectURL(res))
      ),
      send({ url: `${apiUrl}/common/favicon/${organization}`, method: "GET", returnType: "blob" }).then(res =>
        res && setFavicon(URL.createObjectURL(res))
      )
    ])
  }

  useEffect(() => {
    const init = async () => {
      await Promise.all([
        getWeather(),
        getTime(),
        getIcon(),
      ])
    }

    init()
  }, [])

  useLoop(getTime, 1000)
  useLoop(getWeather, 120000)

  const selectProject = (project) => {
    setProjectModelOpen(false)
    
    navigate(`/portal/${organization}/${project}`)
    
    reload()
  }

  const changeLanguage = (lang) => {
    setLanguageModelOpen(false)

    i18n.changeLanguage(lang)

    reload()
  }

  const onFinish = async (values) => {
    if (values["newPassword"] !== values["confirmNewPassword"]) {
      showError(t("signup_passwordNotMatch"))
      return
    }

    setAppbarLoading(true)

    const res = await send({
      method: "POST",
      url: `${apiUrl}/user/change_password`,
      contentType: "json",
      body: {
        "email": user.email,
        "original_password": values["originalPassword"],
        "new_password": values["newPassword"]
      }
    })

    if (res != null) {
      showSuccess(t("general_success"))
      form.resetFields()
      setChangePasswordModalOpen(false)
    }

    setAppbarLoading(false)
  }

  const handleClick = (event) => {
    if (!event.defaultPrevented && event.button === 0 && !(event.metaKey || event.altKey || event.ctrlKey || event.shiftKey)) {
      event.preventDefault()
      navigate(`/portal/${organization}${project ? `/${project}` : ""}`)
    }
  }

  const openUserGuide = async () => {
    setAppbarLoading(true)
      
    const blob = await send({
      method: "GET",
      url: `${apiUrl}/common/user_guide`,
      returnType: "blob"
    })

    if (blob != null) {
      saveAs(blob, `Build4Smart_CMP_User_Guide.pdf`)
    }

    setAppbarLoading(false)
  }

  return (<>
    <Box sx={{ height: appbarHeight, width: "100%", display: "flex", alignItems: "center", paddingLeft: "10px", paddingRight: "10px", overflow: "hidden", borderBottom: "solid 0.5px grey" }}>
      <Stack justifyContent="space-between" direction="row" spacing={5} style={{ height: "100%", width: "100%" }}>
        <div style={{ height: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
          <Box sx={{ display: { xs: 'none', lg: 'flex' }, height: "100%", justifyContent: "center", alignItems: "center" }}>
            <a href={`${webUrl}/portal/${organization}${project ? `/${project}` : ""}`} onClick={(e) => handleClick(e)} style={{ height: "80%", cursor: "pointer" }} target="_blank" rel="noopener noreferrer">
              <img alt="icon" src={icon} style={{ height: "100%", cursor: "pointer" }} />
            </a>
          </Box>

          <Box sx={{ display: { xs: 'flex', lg: 'none' }, height: "100%", justifyContent: "center", alignItems: "center" }}>
            <a href={`${webUrl}/portal/${organization}${project ? `/${project}` : ""}`} onClick={(e) => handleClick(e)} style={{ height: "80%", cursor: "pointer" }} target="_blank" rel="noopener noreferrer">
              <img alt="icon" src={favicon} style={{ height: "100%", cursor: "pointer" }} onClick={() => navigate(".")}/>
            </a>
          </Box>
        </div>

        <div style={{ height: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
          <Typography sx={{ display: '-webkit-box', overflow: 'hidden', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2 }} variant="body1">
            {title}
          </Typography>
        </div>

        <Stack justifyContent="space-between" direction="row" spacing={2} style={{ height: "100%", justifyContent: "center", alignItems: "center" }}>
          <Box sx={{ height: "100%", display: { xs: 'none', md: 'flex' }, flexDirection: 'column' , justifyContent: "center", alignItems: "center" }}>
            <div style={{ height: "50%", display: "flex", justifyContent: "center", alignItems: "center" }}>
              {time &&
                <div style={{ textAlign: "center", whiteSpace: "nowrap" }}>{time.locale(i18n.language.toLowerCase()).format("LLLL")}</div>
              }
            </div>

            <div style={{ height: "50%", display: "flex", justifyContent: "center", alignItems: "center" }}>
              <Stack justifyContent="space-between" direction="row" spacing={2} style={{ height: "100%", justifyContent: "center", alignItems: "center" }}>
                {weather && <>
                  {weather["info"] ? (<>
                    <img alt="weature_icon" src={weather["info"]["icon"]} style={{ height: "20px"}} />

                      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", whiteSpace: "nowrap" }}>
                      <FaTemperatureFull size={20} /> {weather["info"]["temperature"]}°C
                    </div>

                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", whiteSpace: "nowrap" }}>
                      <IoRainy size={20} /> {weather["info"]["rainfall"]}mm
                    </div>

                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", whiteSpace: "nowrap" }}>
                      <IoIosWater size={20} /> {weather["info"]["humidity"]}%
                    </div>
                  </>) : (<div></div>)}

                  {weather["warning"] ? (<>
                    {weather["warning"].map((i) => (
                      <img key={i} alt="weather_warning" src={i} style={{ height: "20px"}} />
                    ))}
                  </>) : (<div></div>)}
                </>}
              </Stack>
            </div>
          </Box>

          {(mode === "portal") && <>
            <IconButtonComponent title={t("general_project")} onClick={() => setProjectModelOpen(true)}>
              <PiList size={20}/>
            </IconButtonComponent>
          </>}

          {(role === "admin") && <>
            {mode === "portal" ? (
              <IconButtonComponent title={t("module_adminConsole")} onClick={() => navigate(`/admin_console/${organization}`)}>
                <RiAdminFill size={20}/>
              </IconButtonComponent>
            ) : (mode === "admin_console") ? (
              <IconButtonComponent title={t("module_webConsole")} onClick={() => navigate(`/portal/${organization}`)}>
                <MdViewModule size={20}/>
              </IconButtonComponent>
            ) : (<></>)}
          </>}

          <IconButtonComponent title={t("general_settings")} onClick={(event) => setAnchorEl(event.currentTarget)}>
            <IoSettingsSharp size={20}/>
          </IconButtonComponent>
        </Stack>
      </Stack>
    </Box>

    <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)} MenuListProps={{ sx: { py: 0 } }}>
      <List style={{ padding: "0px" }}>
        <ListItem>
          <ListItemAvatar>
            <Avatar src={user.picture} />
          </ListItemAvatar>
          <ListItemText
            primary={userMetaData ? userMetaData["display_name"] : ""}
            secondary={<Typography sx={{ display: '-webkit-box', overflow: 'hidden', WebkitBoxOrient: 'vertical', WebkitLineClamp: 1 }}>{user.email}</Typography>}
          />
        </ListItem>

        <Divider sx={{ bgcolor: "secondary.light" }}/>  

        {(mode === "portal") && <> 
          <ListItemButton onClick={() => openUserGuide()}>
            <ListItemIcon>
              <IoDocumentText size={20}/>
            </ListItemIcon>
            <ListItemText primary={t("general_userGuide")}/>
          </ListItemButton> 
        </>}

        <ListItemButton onClick={() => { setLanguageModelOpen(true); setAnchorEl(null); }}>
          <ListItemIcon>
            <MdLanguage size={20} />
          </ListItemIcon>
          <ListItemText primary={t("general_language")}/>
        </ListItemButton>

        <ListItemButton onClick={() => { loginWithRedirect({ appState: { returnTo: webUrl } }) }}>
          <ListItemIcon>
            <GoArrowSwitch size={20}/>
          </ListItemIcon>
          <ListItemText primary={t("general_switchOrginization")}/>
        </ListItemButton>

        <ListItemButton onClick={() => { setChangePasswordModalOpen(true) }}>
          <ListItemIcon>
            <MdOutlineChangeCircle size={20}/>
          </ListItemIcon>
          <ListItemText primary={t("general_changePassword")}/>
        </ListItemButton>

        <ListItemButton onClick={() => { logout({ logoutParams: { returnTo: webUrl } }) }}>
          <ListItemIcon>
            <IoIosLogOut size={20}/>
          </ListItemIcon>
          <ListItemText primary={t("general_logout")}/>
        </ListItemButton>
      </List>          
    </Menu>

    {(mode === "portal") && 
      <PopupModal maxHeight="80%" width="80%" isOpen={projectModelOpen} close toggle={() => setProjectModelOpen(false)}>
        <ListGroup variant="flush">
          {(projects ?? []).map((i) => 
            <ListGroup.Item key={i} className="border-0" action onClick={() => selectProject(`${i["project_code"]}`)}>
              {`${i[i18n.language.toLowerCase()]}`} 
            </ListGroup.Item>
          )}
        </ListGroup>
      </PopupModal>
    }
  
    <PopupModal maxHeight="80%" width="80%" isOpen={languageModelOpen} close toggle={() => setLanguageModelOpen(false)}>
      <ListGroup variant="flush">
        {(["en", "zh"]).map((i) => 
          <ListGroup.Item key={i} className="border-0" action onClick={() => changeLanguage(i)}>
            {t(`language_${i}`)}
          </ListGroup.Item>
        )}
      </ListGroup>
    </PopupModal>

    <PopupModal maxHeight="80%" width="80%" isOpen={changePasswordModalOpen} close toggle={() => setChangePasswordModalOpen(false)}>
      <FormContainer form={form} onFinish={onFinish} initialValues={{}}>
        <PasswordInput field_name={t("general_originalPassword")} field_id="originalPassword" required/>
        <PasswordInput field_name={t("general_newPassword")} field_id="newPassword" required/>
        <PasswordInput field_name={t("general_confirmNewPassword")} field_id="confirmNewPassword" required/>
      </FormContainer>
    </PopupModal>
  </>)
}

export default Appbar
